'use client';
import Price from '@/components/price/Price';
import Image from 'next/image';
import { MODAL_TYPES, useModal } from '@/components/modal/ModalProvider';
import { useRef, useEffect } from 'react';
import {
  useProduct,
  useAdditionalProducts
} from '@/components/product/product-context';
import useButtonFeedbackFlash from './useButtonFeedbackFlash';
import { cleanProductGID } from '@/utils/cleanGIDs';
import clsx from 'clsx';

export default function CrossSellContents({
  filteredVariants,
  blockCrossSellProducts,
  position
}) {
  const { crossSellProducts } = useProduct();
  const { additionalProducts, setAdditionalProducts } = useAdditionalProducts();

  const { showModal } = useModal();

  function addCrossSellProduct(e, variant) {
    e.preventDefault();
    const addedProduct = crossSellProducts.find(
      (product) => product.id == variant.productId
    );

    const variantObj = reshapeVariantForCart(variant, addedProduct);
    if (
      variantObj &&
      !additionalProducts.find((item) => item.productId == variant.productId)
    ) {
      setAdditionalProducts([...additionalProducts, variantObj]);
    }
  }

  function removeCrossSellProduct(e, variant) {
    e.preventDefault();
    const newPayloadArr = additionalProducts.filter(
      (item) => item.productId !== variant.productId
    );
    setAdditionalProducts(newPayloadArr);
  }

  return (
    <div className="mt-5 text-white">
      <ul>
        {filteredVariants.map((variant, index) => {
          if (!hasRequiredFields(variant)) {
            console.warn(
              'This cross-sell variant is missing required fields:',
              variant
            );
            return null;
          }

          const isOutOfStock =
            variant.outOfStock?.value?.toLowerCase() === 'true';

          const isVariantAdded = additionalProducts.find(
            (item) => item.title === variant.productTitle
          );
          const addedProduct = crossSellProducts.find(
            (product) => product.id == variant.productId
          );

          const productDetailsBlocks = blockCrossSellProducts?.find(
            (item) =>
              item?.reference?.[0]?.product_id ===
              cleanProductGID(addedProduct.id)
          )?.reference?.[0]?.product_detail_blocks;

          function showCrossSell(e, variantData, modalIndex) {
            e.preventDefault();
            showModal(MODAL_TYPES.crossSell, {
              variant: variantData,
              index: modalIndex,
              addCrossSellProduct,
              removeCrossSellProduct,
              payloadArr: additionalProducts,
              productDetailsBlocks,
              position,
              isVariantAdded
            });
          }

          if (!isOutOfStock) {
            return (
              <li className={`flex justify-between not-last:pb-6`} key={index}>
                <InfoCard
                  variant={variant}
                  showModal={showCrossSell}
                  index={index}
                />
                <ToggleButton
                  variant={variant}
                  isVariantAdded={!!isVariantAdded}
                  addCrossSellProduct={addCrossSellProduct}
                  removeCrossSellProduct={removeCrossSellProduct}
                />
              </li>
            );
          }
        })}
      </ul>
    </div>
  );
}

/**
 * Super basic validation to check for fields that would crash the cart or break
 * this cross sells component if they were missing.
 */
function hasRequiredFields(variant) {
  return (
    variant &&
    variant.id &&
    variant.productTitle &&
    variant.productHandle &&
    variant.title &&
    variant.price?.amount &&
    variant.compareAtPrice?.amount
  );
}

const InfoCard = ({ variant, showModal, index }) => {
  return (
    <div className="flex">
      <div
        className="relative mr-5 h-20 w-20 cursor-pointer"
        onClick={(e) => showModal(e, variant, index)}
      >
        {variant.image?.url && (
          <Image
            src={variant.image.url}
            alt={variant.productHandle.replace(/\.[^/.]+$/, '')}
            width={80}
            height={80}
            sizes="80px"
            className="min-w-20"
          />
        )}
      </div>
      <div className="flex flex-col justify-between">
        <div
          className="cursor-pointer"
          onClick={(e) => showModal(e, variant, index)}
        >
          <div className="flex">
            <span className="body-s underline">{variant.productTitle} </span>
            <button className="mx-1 mt-1 h-4 w-4">
              <SvgInfo productTitle={variant.productTitle} />
            </button>
          </div>
          <div className="body-xs text-dawn-grey-60">{variant.title}</div>
        </div>
        <Price
          price={variant.price.amount}
          compareAtPrice={variant.compareAtPrice.amount}
          pdpCrossSell={true}
          crossedPriceClassOverride={`price-s-crossed text-dawn-grey-60! pr-1`}
          variant={`cross-sell-${variant.productTitle}`}
        />
      </div>
    </div>
  );
};

function ToggleButton({
  variant,
  isVariantAdded,
  addCrossSellProduct,
  removeCrossSellProduct
}) {
  const { feedbackClass, flash } = useButtonFeedbackFlash();
  const buttonDefaultClass = isVariantAdded ? 'remove-icon' : 'add-icon';

  // Flash when isVariantAdded state toggles
  const wasAddedRef = useRef(!!isVariantAdded);
  useEffect(() => {
    if (!wasAddedRef.current && isVariantAdded) {
      flash('added-icon');
    } else if (wasAddedRef.current && !isVariantAdded) {
      flash('removed-icon');
    }
    wasAddedRef.current = !!isVariantAdded;
  }, [isVariantAdded, flash]);

  const handleClick = (e) => {
    if (isVariantAdded) {
      removeCrossSellProduct(e, variant);
    } else {
      addCrossSellProduct(e, variant);
    }
  };

  return (
    <label className="relative">
      <button
        onClick={handleClick}
        className={clsx(
          'mr-1 h-8 w-8 transition-all duration-300 ease-in-out',
          feedbackClass || buttonDefaultClass
        )}
        aria-label={`${isVariantAdded ? 'Remove' : 'Add'} ${variant.productTitle} - ${variant.title} for $${variant.price.amount}`}
        data-cy="cross-sell-item"
      ></button>
    </label>
  );
}

/**
 * Converts a variant object into a format suitable for adding to the cart.
 * Returns null if required fields are missing.
 */
export function reshapeVariantForCart(variant, product) {
  if (!hasRequiredFields(variant)) {
    return null;
  }
  const price = parseInt(variant.price.amount);
  const compareAt = parseInt(variant.compareAtPrice.amount);
  const discountAmount = Math.max(compareAt - price, 0);

  const variantObj = {
    customAttributes: [
      { key: '_discount_amount', value: (discountAmount * 100).toString() },
      { key: '_productImage', value: variant.productImage },
      { key: '_productPosition', value: '0' },
      { key: '_productType', value: variant.type },
      { key: '_compareAtPrice', value: variant.compareAtPrice.amount },
      { key: '_isCrossSellItem', value: 'true' }
    ],
    handle: variant.productHandle,
    id: variant.id,
    image: variant.productImage,
    price: variant.price,
    compareAtPrice: variant.compareAtPrice,
    title: product.title,
    variantTitle: variant.title,
    sku: variant.sku,
    type: variant.type,
    quantity: 1,
    selectedOptions: variant.selectedOptions,
    availableForSale: variant.availableForSale,
    productId: product.id,
    product: product
  };

  return variantObj;
}

const SvgInfo = ({ productTitle }) => (
  <svg
    width="16px"
    height="16px"
    enableBackground="new 0 0 24 24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-label={`More Info about ${productTitle}`}
  >
    <path
      d="m11 11h1v5m-1.5 0h1.5m1.5 0h-1.5m0 5c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9z"
      fill="none"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="11.9" cy="8.1" fill="#fff" r=".9" />
  </svg>
);
